import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { GetOptions, QueryConstraint } from "@/core";
import { Customer } from "@/core/entities/customer";
import { useCustomers } from "@/hooks/repository-hooks/customers/use-customers";
import { format } from "date-fns";
import {
  Eye,
  Loader2,
  MoreHorizontal,
  RefreshCw,
  Search,
  TrendingUp,
} from "lucide-react";
import { useMemo, useState } from "react";
import { CustomerDetailsSheet } from "./CustomerDetailsSheet";
import { EventsDialog } from "./EventsDialog";
import { MetricsDialog } from "./MetricsDialog";
// import { AuditLogDialog } from "@/components/audit-log/AuditLogDialog";

export default function CustomersTable() {
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [eventsDialogOpen, setEventsDialogOpen] = useState(false);
  const [metricsDialogOpen, setMetricsDialogOpen] = useState(false);
  // const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null,
  );
  const [searchTerm, setSearchTerm] = useState("");
  const pageSize = 10;

  const queryOptions: GetOptions = useMemo(() => {
    const constraints: QueryConstraint[] = [];

    if (searchTerm.trim()) {
      // Use startAt/endAt pattern for efficient prefix search
      const searchText = searchTerm.trim();
      constraints.push(
        { field: "email", operator: ">=", value: searchText },
        { field: "email", operator: "<=", value: searchText + "\uf8ff" },
      );
    }

    return {
      queryConstraints: constraints,
      pagination: {
        limit: pageSize,
      },
      orderBy: {
        field: searchTerm.trim() ? "email" : "updatedAt",
        direction: "desc",
      },
    };
  }, [searchTerm, pageSize]);

  const customersData = useCustomers(queryOptions);

  const customers = useMemo(() => {
    if (!customersData.data) {
      return [];
    }
    // Flatten all pages
    const allCustomers = customersData.data.pages.flat();

    // Additional client-side filtering for lastName and email when searching
    let filteredCustomers = allCustomers;
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filteredCustomers = allCustomers.filter(
        (customer) =>
          customer.firstName.toLowerCase().includes(searchLower) ||
          customer.lastName.toLowerCase().includes(searchLower) ||
          customer.email.toLowerCase().includes(searchLower),
      );
    }

    if (!searchTerm.trim()) {
      return filteredCustomers;
    }

    return [...filteredCustomers].sort(
      (a, b) => b.updatedAt.getTime() - a.updatedAt.getTime(),
    );
  }, [customersData.data, searchTerm]);

  const handleViewEvent = (customer: Customer) => {
    setSelectedCustomer(customer);
    setEventsDialogOpen(true);
  };

  const handleViewDetails = (customer: Customer) => {
    setSelectedCustomer(customer);
    setDetailsDialogOpen(true);
  };

  const handleViewMetrics = (customer: Customer) => {
    setSelectedCustomer(customer);
    setMetricsDialogOpen(true);
  };

  const handleRefresh = () => {
    customersData.refetch();
  };

  if (customersData.error) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-destructive">
          Error loading customers: {customersData.error.message}
        </div>
      </div>
    );
  }

  const renderCustomersContent = () => {
    if (customersData.isLoading) {
      return (
        <div className="flex items-center justify-center p-8">
          <div className="text-muted-foreground">Loading customers...</div>
        </div>
      );
    }

    if (!customers || customers.length === 0) {
      return (
        <div className="flex items-center justify-center p-8">
          <div className="text-muted-foreground">No customers found</div>
        </div>
      );
    }

    return (
      <Table>
        <TableHeader>
          <TableRow className="border-b">
            <TableHead className="relative group whitespace-nowrap text-left border-r last:border-r-0 px-2">
              <div className="flex items-center justify-start">First Name</div>
            </TableHead>
            <TableHead className="relative group whitespace-nowrap text-left border-r last:border-r-0 px-2">
              <div className="flex items-center justify-start">Last Name</div>
            </TableHead>
            <TableHead className="relative group whitespace-nowrap text-left border-r last:border-r-0 px-2">
              <div className="flex items-center justify-start">Email</div>
            </TableHead>
            <TableHead className="relative group whitespace-nowrap text-left border-r last:border-r-0 px-2">
              <div className="flex items-center justify-start">Phone</div>
            </TableHead>
            <TableHead className="relative group whitespace-nowrap text-left border-r last:border-r-0 px-2">
              <div className="flex items-center justify-start">Created At</div>
            </TableHead>
            <TableHead className="relative group whitespace-nowrap text-left border-r last:border-r-0 px-2">
              <div className="flex items-center justify-start">Updated At</div>
            </TableHead>
            <TableHead className="relative group whitespace-nowrap text-center border-r last:border-r-0 w-12">
              <div className="flex items-center justify-center"></div>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {customers.map((customer) => (
            <TableRow key={customer.id} className="hover:bg-muted/50 border-b">
              <TableCell className="whitespace-nowrap text-left border-r last:border-r-0 font-medium">
                {customer.firstName}
              </TableCell>
              <TableCell className="whitespace-nowrap text-left border-r last:border-r-0 font-medium">
                {customer.lastName}
              </TableCell>
              <TableCell className="whitespace-nowrap text-left border-r last:border-r-0">
                {customer.email}
              </TableCell>
              <TableCell className="whitespace-nowrap text-left border-r last:border-r-0 ">
                {customer.phone}
              </TableCell>
              <TableCell className="whitespace-nowrap text-left border-r last:border-r-0 text-muted-foreground">
                {format(new Date(customer.createdAt), "MMM dd, yyyy")}
              </TableCell>
              <TableCell className="whitespace-nowrap text-left border-r last:border-r-0 text-muted-foreground">
                {format(new Date(customer.updatedAt), "MMM dd, yyyy")}
              </TableCell>
              <TableCell className="whitespace-nowrap text-center border-r last:border-r-0 px-0 w-12">
                <div className="flex items-center justify-center">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0">
                        <span className="sr-only">Open menu</span>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                      <DropdownMenuItem
                        onClick={() => handleViewDetails(customer)}
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        View Details
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleViewEvent(customer)}
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        View Events
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleViewMetrics(customer)}
                      >
                        <TrendingUp className="mr-2 h-4 w-4" />
                        View Metrics
                      </DropdownMenuItem>
                      {/* <DropdownMenuItem onClick={() => {
                        setSelectedCustomer(customer);
                        setAuditLogOpen(true);
                      }}>
                        <History className="mr-2 h-4 w-4" />
                        Audit Log
                      </DropdownMenuItem> */}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <h2 className="text-2xl font-bold mb-4">Leads</h2>
          <h3 className="text-lg ml-2 text-muted-foreground self-start mt-1">
            ({customers?.length || 0})
          </h3>
        </div>
      </div>

      {/* Search Input */}
      <div className="flex items-center justify-between">
        <div className="relative flex-1 max-w-sm items-center">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          className="py-4"
          disabled={customersData.isLoading || customersData.isFetching}
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${customersData.isFetching ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
      </div>
      <div className="rounded-md border overflow-hidden overflow-x-auto">
        {renderCustomersContent()}
      </div>

      {/* Load More Button */}
      {customersData.hasNextPage && (
        <div className="flex items-center justify-center py-4">
          <Button
            variant="outline"
            onClick={() => customersData.fetchNextPage()}
            disabled={customersData.isFetchingNextPage}
          >
            {customersData.isFetchingNextPage ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading more...
              </>
            ) : (
              "Load More"
            )}
          </Button>
        </div>
      )}

      {selectedCustomer && (
        <CustomerDetailsSheet
          open={detailsDialogOpen}
          onOpenChange={setDetailsDialogOpen}
          customer={selectedCustomer}
        />
      )}

      {/* Events Dialog */}
      {selectedCustomer && (
        <EventsDialog
          open={eventsDialogOpen}
          onOpenChange={setEventsDialogOpen}
          customer={selectedCustomer}
        />
      )}

      {/* Metrics Dialog */}
      {selectedCustomer && (
        <MetricsDialog
          open={metricsDialogOpen}
          onOpenChange={setMetricsDialogOpen}
          customer={selectedCustomer}
        />
      )}

      {/* Audit Log Dialog */}
      {/* {selectedCustomer && (
        <AuditLogDialog
          open={auditLogOpen}
          onOpenChange={setAuditLogOpen}
          documentId={selectedCustomer.id}
          documentName={`${selectedCustomer.firstName} ${selectedCustomer.lastName}`}
        />
      )} */}
    </div>
  );
}
